import { IosShareOutlined } from "@mui/icons-material"
import Button from "@mui/material/Button"
import IconButton from "@mui/material/IconButton"
import Typography from "@mui/material/Typography"
import { AdvisorIntegrationFYIConnectModalContent } from "features/org/edit/integrations/FYI/AdvisorIntegrationFYI"
import AdvisorIntegrationFYIStore from "features/org/edit/integrations/FYI/AdvisorIntegrationFYIStore"
import { GapDialogChildOptions, GapDialogStore } from "reusable/gap-dialog/GapDialogStore"
import { createStoreContext } from "reusable/mobx-store-utils/storeUtils"
import { themeConfig } from "@gap/shared/theme"
import ShareDocumentDialogFYISelectClient from "../FYI/ShareDocumentDialogFYISelectClient"
import { GapAutocompleteOption } from "features/gap-components/autocomplete/types/GapAutocompleteTypes"
import { messagePromise } from "reusable/mui/modals/message-promise"
import ShareDocumentDialogStore from "../ShareDocumentDialogStore"
import { notNull } from "@gap/utils/utils"
import Tooltip from "@mui/material/Tooltip"
import Loader from "reusable/mobx-helpers/Loader"
import ShareAnalytics from "analytics/ShareAnalytics"
import { downloadFile } from "@gap/utils/file/download-file"
import { snackbarError } from "reusable/mui/snackbar/snackbar"
import DateUtils, { Timeouts } from "@gap/utils/date-utils"
import { postgresApiClient } from "api/trpc-pg/trpc-client"

interface ShareDocumentDialogExportStoreProps {
   advisorIntegrationFYIStore: AdvisorIntegrationFYIStore
   gapDialogStore: GapDialogStore
   shareDocumentDialogStore: ShareDocumentDialogStore
}

export default class ShareDocumentDialogExportStore {
   sp
   downloadDocumentLoader = new Loader()

   constructor(sp: ShareDocumentDialogExportStoreProps) {
      this.sp = sp
   }

   get FYIClientOptions(): GapAutocompleteOption[] {
      return (this.sp.advisorIntegrationFYIStore.FYIClients || []).map(x => ({
         id: `${x.id}`,
         label: x.name
      }))
   }

   get dialogChildConnectOptions(): GapDialogChildOptions {
      return {
         title: "Connect to FYI Docs",
         subTitle: 'Store client files directly in FYI Docs',
         primaryButton: {
            name: 'Connect',
            notCloseAutomatically: true,
            callback: async () => {
               if (this.sp.advisorIntegrationFYIStore.validate()) {
                  this.sp.gapDialogStore.setChildContent(null)
                  await this.sp.advisorIntegrationFYIStore.connect()
                  this.sp.advisorIntegrationFYIStore.resetFields()
               }
            }
         }
      }
   }

   get dialogChildShareOptions(): GapDialogChildOptions {
      return {
         title: "Share to FYI Docs",
         subTitle: 'Store client files directly in FYI Docs',
         primaryButton: {
            name: 'Share',
            notCloseAutomatically: true,
            callback: async () => {
               if (!this.sp.shareDocumentDialogStore.fields.selectedFYIClientId) {
                  this.sp.shareDocumentDialogStore.formHandler.selectedFYIClientId.setError('Required*')
                  return
               }
               this.sp.gapDialogStore.setChildContent(null)
               this.sp.shareDocumentDialogStore.sp.modalState.close()
               messagePromise({
                  contentOnFailure: <>
                     <Typography variant="body1" sx={{ color: themeConfig.primary[900], textAlign: 'center' }}>We had trouble sharing "{this.document.name}"" to FYI Docs</Typography>
                     <Typography variant="caption" sx={{ color: themeConfig.primary[600], mt: 0.5 }}>Please try again or contact support if the issue persists.</Typography>
                  </>,
                  withRetry: true,
                  contentOnLoad: "Sharing to FYI Docs",
                  contentOnSuccess: `"${this.document.name}" successfully shared to FYI Docs`,
                  promiseCallback: this.shareDocumentToFYI
               })
            }
         }
      }
   }

   get document() {
      return this.sp.shareDocumentDialogStore.documentData.document
   }

   get FYICardRightComponent() {
      if (this.sp.advisorIntegrationFYIStore.connectLoader.isLoading) {
         return {
            component: <Typography sx={{ mt: '10px' }}>Connecting...</Typography>,
            handler: () => { }
         }
      }

      if (this.sp.advisorIntegrationFYIStore.status === 'connected') {
         return {
            component: <IconButton>
               <IosShareOutlined sx={{ color: themeConfig.primary['800'] }} />
            </IconButton>,
            handler: this.onFYIShareClick
         }
      }

      const hasPermissionToConnect = this.sp.shareDocumentDialogStore.sp.orgGuardStore.userHasMyOrganisationPermission

      if (hasPermissionToConnect) {
         return {
            component: <Button variant="text" sx={{ mt: 0.5 }}>
               Connect
            </Button>,
            handler: this.onFYIConnectClick
         }
      }

      return {
         component: <Tooltip title="You don't have access to set up this connection. Please contact an admin from your team to set this up." placement="left">
            <span>
               <Button variant="text" sx={{ mt: 0.5 }} disabled>
                  Connect
               </Button>
            </span>
         </Tooltip>,
         handler: () => { }
      }
   }

   downloadDocument = async () => {
      await this.downloadDocumentLoader.executeLoading(async () => {
         const result = await this.awaitFile()
         if (result?.url) {
            const fileDataResponse = await fetch(result.url)
            if (fileDataResponse.ok) {
               const file = await fileDataResponse.blob()
               downloadFile(file, result.name)
               ShareAnalytics.documentShared({
                  "Initiated from": this.sp.shareDocumentDialogStore.sp.source,
                  "Share action": 'Exported to PDF'
               })
               return
            }
         }

         snackbarError('Download failed. If the issue persists, please contact support.')
      })
   }

   shareDocumentToFYI = async () => {
      // Ensure file exists before pushing to FYI
      const result = await this.awaitFile()
      if (result?.url) {
         await postgresApiClient.docShare.shareDocumentWithFYI.mutate({
            clientOrgId: this.sp.shareDocumentDialogStore.sp.client.orgId,
            document: this.sp.shareDocumentDialogStore.documentData.document,
            FYIClientId: notNull(this.sp.shareDocumentDialogStore.fields.selectedFYIClientId)
         })
         ShareAnalytics.documentShared({
            "Initiated from": this.sp.shareDocumentDialogStore.sp.source,
            "Share action": 'Exported to FYI'
         })
      } else {
         // Throw an error to trigger the failure state of the message promise. User can click try again to retry.
         throw new Error('PDF file was not found')
      }

   }

   onFYIConnectClick = () => {
      this.sp.gapDialogStore.setChildContent(<AdvisorIntegrationFYIConnectModalContent />, this.dialogChildConnectOptions)
   }

   onFYIShareClick = () => {
      this.sp.gapDialogStore.setChildContent(<ShareDocumentDialogFYISelectClient shareDocumentDialogExportStore={this} />, this.dialogChildShareOptions)
   }

   private awaitFile = async (attemptNumber: number = 1): Promise<{ url: string, name: string } | null> => {
      const { url, name, exists } = await postgresApiClient.docShare.getDocumentDownloadLink.query({
         document: this.document,
         clientOrgId: this.sp.shareDocumentDialogStore.sp.client.orgId
      })
      if (exists) {
         return { url, name }
      }
      if (attemptNumber < 3) {
         await DateUtils.wait(Timeouts.second * 5)
         return await this.awaitFile(attemptNumber + 1)
      }
      return null
   }
}

export const { Provider: ShareDocumentDialogExportStoreProvider, useProvidedStore: useShareDocumentDialogExportStore } = createStoreContext(ShareDocumentDialogExportStore)
